export interface RawAdjudicationPromptTuning {
  minCleanConfidence?: number;
  assessmentTokenBudget?: number;
  truncatedReasoningLength?: number;
}

export interface AdjudicationPromptTuning {
  minCleanConfidence: number;
  assessmentTokenBudget: number;
  truncatedReasoningLength: number;
}

/**
 * Canonical defaults for adjudication prompt formatting/runtime tuning. These values
 * should stay in sync with the documented defaults exposed through site_configs.
 */
export const DEFAULT_ADJUDICATION_PROMPT_TUNING: AdjudicationPromptTuning = {
  minCleanConfidence: 0.9,
  assessmentTokenBudget: 1800,
  truncatedReasoningLength: 80,
};

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isPositiveInteger(value: number): boolean {
  return Number.isInteger(value) && value > 0;
}

function resolveNumber(value: unknown, fallback: number, isValid: (candidate: number) => boolean): number {
  if (!isFiniteNumber(value) || !isValid(value)) {
    return fallback;
  }
  return value;
}

/**
 * Resolves raw adjudication prompt tuning config from site_configs into a complete,
 * validated runtime object. Missing or invalid values fall back to documented defaults.
 */
export function resolveAdjudicationPromptTuning(raw: RawAdjudicationPromptTuning | undefined): AdjudicationPromptTuning {
  return {
    minCleanConfidence: resolveNumber(
      raw?.minCleanConfidence,
      DEFAULT_ADJUDICATION_PROMPT_TUNING.minCleanConfidence,
      (value) => value >= 0 && value <= 1,
    ),
    assessmentTokenBudget: resolveNumber(
      raw?.assessmentTokenBudget,
      DEFAULT_ADJUDICATION_PROMPT_TUNING.assessmentTokenBudget,
      isPositiveInteger,
    ),
    truncatedReasoningLength: resolveNumber(
      raw?.truncatedReasoningLength,
      DEFAULT_ADJUDICATION_PROMPT_TUNING.truncatedReasoningLength,
      isPositiveInteger,
    ),
  };
}
